import { sleep } from '@moniebook-web/utils';

import { Axios } from '../api';
import { REGISTERS_API_URL } from '../constants';
import { toBooleanValueFields } from '../transform';
import {
    BOOLEAN_VALUE,
    type MappedConfigurationResponse,
    type MappedPaymentMethodResponse,
    MONIEPOINT_TERMINAL_PAYMENT_ID,
    PAYMENT_METHOD_TYPE,
    type PaymentMethodType,
    paymentMethodDisplayOrder,
} from '../types';

import type { AppDatabase } from '../db';
import type { PaymentMethod } from '../models/payment-method';

const PAYMENT_METHOD_SERVICE_WAIT_MS = 10_000; //10 seconds

const localPaymentMethods: PaymentMethod[] = [
    {
        name: 'Moniepoint Terminal',
        type: PAYMENT_METHOD_TYPE.MONIEPOINT_TERMINAL,
        id: MONIEPOINT_TERMINAL_PAYMENT_ID,
        createdAt: '',
        deleted: BOOLEAN_VALUE.FALSE,
        enabled: BOOLEAN_VALUE.TRUE,
        paymentMethodIndex: 1_234_567,
        updatedAt: '',
    },
];

export class PaymentMethodService {
    constructor(private readonly db: AppDatabase) {
        this.db = db;
    }

    async fetchAndBulkUpsert() {
        const response = await Axios.get<MappedConfigurationResponse>(
            REGISTERS_API_URL.REGISTER.CONFIGURATION,
        );

        await this.bulkUpsert(response.data.paymentMethodConfiguration);
    }

    async bulkUpsert(paymentMethods: MappedPaymentMethodResponse[]) {
        const output = paymentMethods.map((method) => {
            return toBooleanValueFields(method, ['deleted', 'enabled']);
        });

        await this.db.paymentMethods.bulkPut(
            output as unknown as PaymentMethod[],
        );
    }

    async getById(id: string) {
        const local = localPaymentMethods.find((method) => method.id === id);
        if (local) {
            return local;
        }

        return this.db.paymentMethods.get(id);
    }

    async getAllByIds(ids: string[]) {
        const localMatches = localPaymentMethods.filter((method) =>
            ids.includes(method.id),
        );
        const dbResults = await this.db.paymentMethods.bulkGet(ids);

        return [...localMatches, ...dbResults.filter((method) => !!method)];
    }

    async getByType(type: PaymentMethodType) {
        return this.db.paymentMethods.where('type').equals(type).first();
    }

    async resolveByType(type: PaymentMethodType) {
        const paymentMethod = await this.getByType(type);
        if (paymentMethod) {
            return paymentMethod;
        }

        await Promise.any([
            this.fetchAndBulkUpsert(),
            sleep(PAYMENT_METHOD_SERVICE_WAIT_MS),
        ]);

        return this.getByType(type);
    }

    async getAll(includeCustomerWallet = false) {
        const output = [];

        const methods = await this.db.paymentMethods
            .where('[enabled+deleted]')
            .equals([BOOLEAN_VALUE.TRUE, BOOLEAN_VALUE.FALSE])
            .filter((method) =>
                method.type === PAYMENT_METHOD_TYPE.CUSTOMER_WALLET
                    ? includeCustomerWallet
                    : true,
            )
            .toArray();

        output.push(...methods, ...localPaymentMethods);

        return output.sort(
            (a, b) =>
                paymentMethodDisplayOrder.indexOf(a.type) -
                paymentMethodDisplayOrder.indexOf(b.type),
        );
    }
}
